import { supabase } from '@/integrations/supabase/client'

interface TenantData {
  id: string
  name: string
  domain: string | null
  landing_page_url: string | null
  slug: string
}

interface CustomerWithTenant {
  id: string
  tenant_id: string
  referral_code: string
  tenants?: TenantData
}

/**
 * Generates a referral URL using the tracking redirect system.
 * All links point to the main app for tracking, then redirect to custom domains if available.
 * 
 * @param referralCode - The customer's referral code
 * @param tenantData - Optional tenant data with custom domain
 * @param useDirectLink - If true, generates direct link (for same-domain usage)
 * @param landingPage - Optional landing page identifier
 * @returns Complete referral URL
 */
export const generateReferralUrl = (
  referralCode: string, 
  tenantData?: TenantData | null, 
  useDirectLink: boolean = false,
  landingPage?: string
): string => {
  const mainAppUrl = 'https://herzensempfehlung.de'
  
  // Debug logging
  console.log('Generating referral URL:', { referralCode, tenantData, useDirectLink, landingPage, mainAppUrl })
  
  // If using direct link (same domain) or no custom landing page/domain
  // BUT always use tracking for landing pages
  if (useDirectLink || (!tenantData?.landing_page_url && !tenantData?.domain && !landingPage)) {
    const finalUrl = `${mainAppUrl}/ref/${referralCode}${landingPage ? `/${landingPage}` : ''}`
    console.log('Direct referral URL:', finalUrl)
    return finalUrl
  }
  
  // Use tracking URL that redirects to custom domain or landing pages
  const trackingUrl = `${mainAppUrl}/track/${referralCode}${landingPage ? `/${landingPage}` : ''}`
  console.log('Tracking referral URL:', trackingUrl)
  return trackingUrl
}

/**
 * Generates a shareable referral URL optimized for external sharing.
 * Always uses the tracking system to ensure clicks are captured.
 */
export const generateShareableReferralUrl = (referralCode: string, tenantData?: TenantData | null, landingPage?: string): string => {
  return generateReferralUrl(referralCode, tenantData, false, landingPage)
}

/**
 * Fetches tenant data for a given customer to enable tenant-specific referral links
 * 
 * @param customerId - The customer's ID
 * @returns Tenant data or null if not found
 */
export const fetchTenantDataForCustomer = async (customerId: string): Promise<TenantData | null> => {
  try {
    // First get the customer data
    const { data: customer, error: customerError } = await supabase
      .from('customers')
      .select('id, tenant_id')
      .eq('id', customerId)
      .single()

    console.log('Fetched customer:', { customer, customerError })

    if (customerError || !customer) {
      console.error('Error fetching customer:', customerError)
      return null
    }

    // Then get the tenant data separately to avoid RLS issues
    const { data: tenant, error: tenantError } = await supabase
      .from('tenants')
      .select('id, name, domain, landing_page_url, slug')
      .eq('id', customer.tenant_id)
      .single()

    console.log('Fetched tenant:', { tenant, tenantError })

    if (tenantError || !tenant) {
      console.error('Error fetching tenant:', tenantError)
      return null
    }

    console.log('Returning tenant data:', tenant)
    return tenant as TenantData
  } catch (error) {
    console.error('Error in fetchTenantDataForCustomer:', error)
    return null
  }
}

/**
 * Validates that a referral code belongs to a customer in the same tenant
 * Simplified version for public access without authentication requirements
 * 
 * @param referralCode - The referral code to validate
 * @param newCustomerTenantId - The tenant ID of the new customer (optional)
 * @returns Validation result with customer and tenant information
 */
export const validateReferralCodeWithTenant = async (
  referralCode: string, 
  newCustomerTenantId?: string
) => {
  try {
    console.log('Validating referral code with secure RPC:', referralCode)
    
    // Use secure RPC function to validate referral code
    const { data: validationData, error: validationError } = await supabase.rpc('validate_referral_code_secure', {
      p_referral_code: referralCode
    })

    console.log('Secure validation result:', { validationData, validationError })

    if (validationError) {
      console.error('RPC validation error:', validationError)
      return {
        valid: false,
        error: 'Fehler bei der Datenbankabfrage'
      }
    }

    if (!validationData || validationData.length === 0) {
      return {
        valid: false,
        error: 'Empfehlungscode nicht gefunden'
      }
    }

    const validation = validationData[0]
    
    if (!validation.valid) {
      return {
        valid: false,
        error: 'Empfehlungscode nicht gefunden'
      }
    }

    // Check if tenant is active
    if (validation.tenant_status !== 'active') {
      return {
        valid: false,
        error: 'Das Unternehmen ist derzeit nicht aktiv'
      }
    }

    // If we have a new customer tenant ID, ensure it matches
    if (newCustomerTenantId && newCustomerTenantId !== validation.tenant_id) {
      return {
        valid: false,
        error: 'Empfehlung ist nicht für dieses Unternehmen gültig'
      }
    }

    return {
      valid: true,
      customer: {
        id: validation.customer_id,
        full_name: validation.customer_name,
        email: validation.customer_email,
        tenant_id: validation.tenant_id,
        referral_code: referralCode
      },
      tenant: {
        id: validation.tenant_id,
        name: validation.tenant_name,
        domain: validation.tenant_domain,
        landing_page_url: null,
        slug: validation.tenant_slug,
        status: validation.tenant_status
      }
    }
  } catch (error) {
    console.error('Error validating referral code:', error)
    return {
      valid: false,
      error: 'Fehler bei der Validierung des Empfehlungscodes'
    }
  }
}

/**
 * Creates a branded referral message for sharing
 * 
 * @param tenantName - Name of the business/tenant
 * @param referralUrl - Complete referral URL
 * @param customerName - Name of the referring customer
 * @returns Formatted message for sharing
 */
export const createReferralMessage = (tenantName: string, referralUrl: string, customerName?: string): string => {
  const referrerName = customerName || 'Ein Kunde'
  return `🎉 ${referrerName} lädt Sie zu ${tenantName} ein! 

Holen Sie sich Ihre digitale Stempelkarte und sammeln Sie bei jedem Besuch Punkte. Wir erhalten beide Vorteile!

${referralUrl}

#DigitaleLoyalty #Stempelkarten #${tenantName.replace(/\s+/g, '')}`
}

/**
 * Tracks referral link usage for analytics with enhanced source tracking
 * 
 * @param referralCode - The referral code used
 * @param source - Where the referral came from (e.g., 'direct_link', 'redirect_tracking', 'qr', 'social')
 * @param userAgent - Optional user agent for tracking
 * @param additionalData - Extra tracking data
 */
export const trackReferralUsage = async (
  referralCode: string, 
  source: string = 'link',
  userAgent?: string,
  additionalData?: Record<string, any>
) => {
  try {
    await supabase
      .from('audit_logs')
      .insert({
        action: 'referral_link_accessed',
        resource_type: 'referral',
        details: {
          referral_code: referralCode,
          source,
          user_agent: userAgent || navigator.userAgent,
          timestamp: new Date().toISOString(),
          tracking_type: source.includes('redirect') ? 'cross_domain' : 'direct',
          ...additionalData
        }
      })
  } catch (error) {
    console.error('Error tracking referral usage:', error)
    // Don't throw - tracking is optional
  }
}